import { createContext, useContext, useState, useEffect, ReactNode } from "react";

const PRICING_VISIBILITY_KEY = "maxPricingVisibility";

interface PricingVisibilityContextType {
  isPricingVisible: boolean;
  togglePricing: () => void;
  setPricingVisible: (visible: boolean) => void;
}

const PricingVisibilityContext = createContext<PricingVisibilityContextType | undefined>(undefined);

export function PricingVisibilityProvider({ children }: { children: ReactNode }) {
  // Initialize from localStorage, default to true (pricing visible)
  const [isPricingVisible, setIsPricingVisible] = useState<boolean>(() => {
    const stored = localStorage.getItem(PRICING_VISIBILITY_KEY);
    return stored === null ? true : stored === "true";
  });

  // Persist to localStorage whenever visibility changes
  useEffect(() => {
    localStorage.setItem(PRICING_VISIBILITY_KEY, isPricingVisible.toString());
  }, [isPricingVisible]);

  const togglePricing = () => {
    setIsPricingVisible(prev => !prev);
  };

  const setPricingVisible = (visible: boolean) => {
    setIsPricingVisible(visible);
  };

  return (
    <PricingVisibilityContext.Provider value={{
      isPricingVisible,
      togglePricing,
      setPricingVisible
    }}>
      {children}
    </PricingVisibilityContext.Provider>
  );
}

export function usePricingVisibility() {
  const context = useContext(PricingVisibilityContext);
  if (context === undefined) {
    throw new Error("usePricingVisibility must be used within a PricingVisibilityProvider");
  }
  return context;
}
